import { useEffect, useState, FormEvent, useContext } from "react";
import { getTokenForWeb, getCustomerDetailsByToken, userLoginOps } from "../services/auth";
import { decrypt, encrypt, getOrCreateDeviceId, validUserLogin } from "../utils/Util";
import { ensureFreshToken } from "../services/tokenRefresh";
import { IUserLogin } from "../models/IUserLogin";
import { Context } from "../utils/AppContext";
import { useNavigate, Link } from "react-router-dom";
import LoginRoleSwitch from "../components/LoginRoleSwitch";

function Login() {
  const navigate = useNavigate();
  const context = useContext(Context);

  const hasSaved = !!(localStorage.username && localStorage.password);
  const [remember, setRemember] = useState(hasSaved);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const [userdata, setUserdata] = useState<IUserLogin>({
    email: localStorage.username ? decrypt(localStorage.username) : "",
    password: localStorage.password ? decrypt(localStorage.password) : "",
    deviceId: getOrCreateDeviceId(),
  });

  useEffect(() => {
    // Web token al
    getTokenForWeb()
      .then((res) => {
        if (res?.data) {
          localStorage.setItem("MA", encrypt(JSON.stringify(res.data)));
        }
      })
      .catch((err) => {
        console.log("error", err.message);
      });

    // Cache'de oturum var mı kontrol et
    const cached = localStorage.getItem("user");
    if (!cached) return;

    // Token süresi geçmişse ama refreshToken hâlâ geçerliyse önce sessizce yenile
    ensureFreshToken()
      .then((ok) => {
        if (!ok) {
          localStorage.removeItem("user");
          return;
        }
        // ensureFreshToken storage'ı güncellemiş olabilir — güncel halini oku
        const raw = localStorage.getItem("user");
        const current = raw ? JSON.parse(decrypt(raw)) : null;
        if (!current) return;
        getCustomerDetailsByToken()
          .then((res) => {
            const merged = { ...current, ...res.data };
            localStorage.setItem("user", encrypt(JSON.stringify(merged)));
            context.setName(merged as any);
            navigate("/dashboard", { replace: true });
          })
          .catch(() => {
            // Token geçersiz veya süresi dolmuş — cache temizle, login göster
            localStorage.removeItem("user");
          });
      })
      .catch(() => localStorage.removeItem("user"));
  }, []);

  const userLogin = (evt: FormEvent) => {
    evt.preventDefault();
    const status = validUserLogin(userdata.email, userdata.password);
    if (!status) return;

    if (remember) {
      localStorage.setItem("username", encrypt(userdata.email));
      localStorage.setItem("password", encrypt(userdata.password));
    } else {
      localStorage.removeItem("username");
      localStorage.removeItem("password");
    }

    setLoading(true);
    userLoginOps(userdata)
      .then(async (res) => {
        const tokenData = res.data;
        localStorage.setItem("user", encrypt(JSON.stringify(tokenData)));
        try {
          const detailsRes = await getCustomerDetailsByToken();
          const merged = { ...tokenData, ...detailsRes.data };
          localStorage.setItem("user", encrypt(JSON.stringify(merged)));
          context.setName(merged as any);
        } catch {
          context.setName(tokenData);
        }
        navigate("/dashboard", { replace: true });
      })
      .catch((err) => {
        const data = err?.response?.data;
        const apiMsg = Array.isArray(data) && data[0]?.description
          ? data[0].description
          : err.message;
        // Garson hesabı yönetici rolü taşımaz; restoran girişinden 404 AdminRoleNotFound alır.
        const code = Array.isArray(data) ? data[0]?.code : undefined;
        setErrorMessage(code === "AdminRoleNotFound"
          ? "Bu hesap restoran yöneticisi değil. Garson iseniz \"Garson\" sekmesinden, kurye iseniz \"Kurye\" sekmesinden giriş yapın."
          : apiMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f4ff 0%, #fafafa 60%, #f0fdf4 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          background: '#fff',
          borderRadius: '20px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
          padding: '44px 40px 36px',
        }}
      >
        {/* Logo / Başlık */}
        <div className="text-center mb-4">
          <div
            style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              borderRadius: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              boxShadow: '0 4px 14px rgba(34,197,94,0.35)',
            }}
          >
            <i className="bi bi-shop-window text-white" style={{ fontSize: '1.6rem' }}></i>
          </div>
          <h4 className="fw-bold mb-1" style={{ color: '#111827', letterSpacing: '-0.3px' }}>
            Mutfacık Admin
          </h4>
          <p className="text-muted small mb-0">Hesabınıza giriş yapın</p>
        </div>

        {/* Restoran / Kurye seçici */}
        <LoginRoleSwitch active="restoran" />

        {/* Hata mesajı */}
        {errorMessage && (
          <div
            className="d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-3"
            style={{ background: '#fef2f2', border: '1.5px solid #fecaca', color: '#b91c1c', fontSize: '0.85rem' }}
          >
            <i className="bi bi-exclamation-circle-fill flex-shrink-0"></i>
            <span>{errorMessage}</span>
            <button
              onClick={() => setErrorMessage("")}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', padding: 0 }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={userLogin}>
          {/* Email */}
          <div className="mb-3">
            <label className="form-label small fw-semibold mb-1" style={{ color: '#374151' }}>
              E-posta
            </label>
            <div className="input-group">
              <span
                className="input-group-text"
                style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRight: 'none', borderRadius: '10px 0 0 10px', color: '#9ca3af' }}
              >
                <i className="bi bi-envelope-fill"></i>
              </span>
              <input
                type="email"
                value={userdata.email}
                onChange={(e) => setUserdata({ ...userdata, email: e.target.value })}
                className="form-control"
                placeholder="ornek@email.com"
                style={{
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRadius: '0 10px 10px 0',
                  fontSize: '0.93rem',
                  padding: '10px 14px',
                  boxShadow: 'none',
                }}
                required
              />
            </div>
          </div>

          {/* Şifre */}
          <div className="mb-3">
            <label className="form-label small fw-semibold mb-1" style={{ color: '#374151' }}>
              Şifre
            </label>
            <div className="input-group">
              <span
                className="input-group-text"
                style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRight: 'none', borderRadius: '10px 0 0 10px', color: '#9ca3af' }}
              >
                <i className="bi bi-lock-fill"></i>
              </span>
              <input
                type={showPass ? "text" : "password"}
                value={userdata.password}
                onChange={(e) => setUserdata({ ...userdata, password: e.target.value })}
                className="form-control"
                placeholder="••••••••"
                style={{
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRight: 'none',
                  fontSize: '0.93rem',
                  padding: '10px 14px',
                  boxShadow: 'none',
                }}
                required
              />
              <button
                type="button"
                className="input-group-text"
                onClick={() => setShowPass(!showPass)}
                style={{
                  background: '#f9fafb',
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRadius: '0 10px 10px 0',
                  color: '#9ca3af',
                  cursor: 'pointer',
                }}
              >
                <i className={`bi ${showPass ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
              </button>
            </div>
          </div>

          {/* Beni Hatırla */}
          <div className="d-flex align-items-center mb-4">
            <div
              onClick={() => setRemember(!remember)}
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '5px',
                border: `2px solid ${remember ? '#22c55e' : '#d1d5db'}`,
                background: remember ? '#22c55e' : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.15s ease',
              }}
            >
              {remember && <i className="bi bi-check text-white" style={{ fontSize: '0.7rem', lineHeight: 1 }}></i>}
            </div>
            <label
              onClick={() => setRemember(!remember)}
              className="ms-2 small"
              style={{ color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}
            >
              Beni hatırla
            </label>
          </div>

          {/* Giriş Butonu */}
          <button
            type="submit"
            className="btn w-100"
            disabled={loading}
            style={{
              background: loading ? '#86efac' : 'linear-gradient(135deg, #22c55e, #16a34a)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.97rem',
              borderRadius: '10px',
              padding: '11px',
              border: 'none',
              boxShadow: '0 4px 14px rgba(34,197,94,0.35)',
              transition: 'all 0.2s ease',
              letterSpacing: '0.2px',
            }}
          >
            {loading ? (
              <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Giriş yapılıyor...</>
            ) : (
              <><i className="bi bi-box-arrow-in-right me-2"></i>Giriş Yap</>
            )}
          </button>
        </form>

        {/* LiveChat temsilcileri için ayrı giriş */}
        <div className="text-center" style={{ marginTop: '20px' }}>
          <Link
            to="/LiveChatLogin"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: '#6366f1', fontWeight: 600, fontSize: '0.85rem',
              textDecoration: 'none',
            }}
          >
            <i className="bi bi-headset"></i> LiveChat temsilcisi misiniz? Buradan giriş yapın
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;